import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSliders,
  createSlider,
  updateSlider,
  deleteSlider,
  reorderSliders,
} from "../../store/slices/sliderSlice";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaArrowUp,
  FaArrowDown,
  FaTimes,
  FaImage,
  FaSave,
  FaToggleOn,
  FaToggleOff,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";

const EMPTY_FORM = {
  title: "",
  subtitle: "",
  link: "",
  order: 0,
  isActive: true,
  imageFile: null,
  imagePreview: "",
};

const AdminSlider = () => {
  const dispatch = useDispatch();
  const { sliders, loading, saving, error } = useSelector((s) => s.slider);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    dispatch(fetchSliders(true));
  }, [dispatch]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM, order: sliders.length });
    setShowForm(true);
  };

  const openEdit = (slide) => {
    setEditId(slide._id);
    setForm({
      title: slide.title || "",
      subtitle: slide.subtitle || "",
      link: slide.link || "",
      order: slide.order ?? 0,
      isActive: slide.isActive,
      imageFile: null,
      imagePreview: slide.imageUrl,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((f) => ({
      ...f,
      imageFile: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editId && !form.imageFile) {
      showToast("Please select a slide image.", "error");
      return;
    }

    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("subtitle", form.subtitle);
    fd.append("link", form.link);
    fd.append("order", form.order);
    fd.append("isActive", form.isActive);
    if (form.imageFile) fd.append("sliderImage", form.imageFile);

    if (editId) {
      const res = await dispatch(updateSlider({ id: editId, formData: fd }));
      if (!res.error) { showToast("Slide updated."); closeForm(); }
      else showToast(res.payload || "Update failed.", "error");
    } else {
      const res = await dispatch(createSlider(fd));
      if (!res.error) { showToast("Slide added."); closeForm(); }
      else showToast(res.payload || "Create failed.", "error");
    }
  };

  const handleDelete = async (id) => {
    const res = await dispatch(deleteSlider(id));
    if (!res.error) showToast("Slide deleted.");
    else showToast(res.payload || "Delete failed.", "error");
    setDeleteConfirm(null);
  };

  const moveSlide = async (idx, dir) => {
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= sliders.length) return;
    const items = sliders.map((s, i) => {
      if (i === idx) return { id: s._id, order: sliders[swapIdx].order };
      if (i === swapIdx) return { id: s._id, order: sliders[idx].order };
      return { id: s._id, order: s.order };
    });
    await dispatch(reorderSliders(items));
    dispatch(fetchSliders(true));
  };

  const toggleActive = async (slide) => {
    const fd = new FormData();
    fd.append("isActive", !slide.isActive);
    const res = await dispatch(updateSlider({ id: slide._id, formData: fd }));
    if (!res.error) showToast(`Slide ${!slide.isActive ? "activated" : "deactivated"}.`);
  };

  return (
    <div className="space-y-5">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-50 px-5 py-3 rounded-xl text-white text-sm font-medium shadow-lg transition-all ${
            toast.type === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Slider Management</h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Manage homepage slider images — {sliders.length} slide{sliders.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition shadow-sm"
        >
          <FaPlus size={12} />
          Add Slide
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 border-4 border-blue-700 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Empty */}
      {!loading && sliders.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaImage className="text-gray-400 text-2xl" />
          </div>
          <p className="text-gray-500 font-medium">No slides yet.</p>
          <p className="text-gray-400 text-sm mt-1">Click "Add Slide" to create the first slide.</p>
        </div>
      )}

      {/* Slides List */}
      {!loading && sliders.length > 0 && (
        <div className="space-y-3">
          {sliders.map((slide, idx) => (
            <div
              key={slide._id}
              className={`bg-white rounded-2xl border flex items-center gap-4 p-4 transition-all ${
                slide.isActive ? "border-gray-100" : "border-dashed border-gray-300 opacity-70"
              }`}
            >
              {/* Thumbnail */}
              <div className="w-28 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                <img
                  src={slide.imageUrl}
                  alt={slide.title || "Slide"}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-gray-900 truncate">
                    {slide.title || <span className="text-gray-400 italic">No title</span>}
                  </p>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                      slide.isActive
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {slide.isActive ? "Active" : "Hidden"}
                  </span>
                </div>
                {slide.subtitle && (
                  <p className="text-gray-500 text-sm mt-0.5 truncate">{slide.subtitle}</p>
                )}
                <p className="text-gray-400 text-xs mt-1">Order: {slide.order}</p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Move up/down */}
                <button
                  onClick={() => moveSlide(idx, -1)}
                  disabled={idx === 0}
                  title="Move up"
                  className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  <FaArrowUp size={13} />
                </button>
                <button
                  onClick={() => moveSlide(idx, 1)}
                  disabled={idx === sliders.length - 1}
                  title="Move down"
                  className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                >
                  <FaArrowDown size={13} />
                </button>

                {/* Toggle active */}
                <button
                  onClick={() => toggleActive(slide)}
                  title={slide.isActive ? "Deactivate" : "Activate"}
                  className={`p-2 rounded-lg transition ${
                    slide.isActive
                      ? "text-green-600 hover:bg-green-50"
                      : "text-gray-400 hover:bg-gray-100"
                  }`}
                >
                  {slide.isActive ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                </button>

                {/* Edit */}
                <button
                  onClick={() => openEdit(slide)}
                  title="Edit"
                  className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition"
                >
                  <FaEdit size={14} />
                </button>

                {/* Delete */}
                <button
                  onClick={() => setDeleteConfirm(slide._id)}
                  title="Delete"
                  className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                >
                  <FaTrash size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeForm}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900 text-lg">
                {editId ? "Edit Slide" : "Add New Slide"}
              </h2>
              <button
                onClick={closeForm}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 transition"
              >
                <FaTimes size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Image upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slide Image {!editId && <span className="text-red-500">*</span>}
                </label>
                {form.imagePreview && (
                  <div className="mb-3 rounded-xl overflow-hidden h-40 bg-gray-100">
                    <img
                      src={form.imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div
                  onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 hover:border-blue-400 rounded-xl p-6 text-center cursor-pointer transition"
                >
                  <FaImage className="text-gray-400 text-2xl mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {form.imageFile ? form.imageFile.name : "Click to select image"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Recommended: 1920×580px, max 5MB
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFile}
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g. Premium Garment Manufacturing"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subtitle <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  name="subtitle"
                  value={form.subtitle}
                  onChange={handleChange}
                  rows={2}
                  placeholder="e.g. Trusted by 500+ clients across India"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Button Link <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  name="link"
                  value={form.link}
                  onChange={handleChange}
                  placeholder="/products or /contact"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  name="order"
                  type="number"
                  min={0}
                  value={form.order}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <input
                  id="isActive"
                  name="isActive"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={handleChange}
                  className="w-4 h-4 accent-blue-600"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Active (visible on homepage)
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="flex-1 border border-gray-200 text-gray-700 font-semibold text-sm py-2.5 rounded-xl hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold text-sm py-2.5 rounded-xl transition disabled:opacity-60"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FaSave size={13} />
                  )}
                  {editId ? "Update Slide" : "Add Slide"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaTrash className="text-red-600 text-xl" />
            </div>
            <h3 className="font-bold text-gray-900 text-lg mb-2">Delete Slide?</h3>
            <p className="text-gray-500 text-sm mb-6">
              This will permanently remove the slide and its image from Cloudinary.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-gray-200 text-gray-700 font-semibold text-sm py-2.5 rounded-xl hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={saving}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm py-2.5 rounded-xl transition disabled:opacity-60"
              >
                {saving ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSlider;
